import { memo, useMemo, useState } from 'react';
import { Badge } from '../../../atoms/Badge/Badge';
import { Button } from '../../../atoms/Button/Button';
import { Icon } from '../../../atoms/Icon/Icon';
import { PlatformIcon } from '../../../atoms/PlatformIcon/PlatformIcon';
import { platformFor } from '@deepdive-icons';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import type { TableHeaderSort } from '../../../molecules/TableHeaderLabel/TableHeaderLabel';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { spanClass, type WidgetBlockProps } from '../../widgets/shared/cardShell';
import { SENTIMENT_BADGE, pageWindow, tableEmptyLabel } from '../shared/tableShared';
import { TableWidgetActions, tableDraggingClass } from '../TableWidgetActions/TableWidgetActions';

/**
 * Community — `Top voices` (2215:107393) and `Top communities by mention`
 * (2215:110242), both added to the frame 2026-09-24.
 *
 * One component, two tables: the file draws them with the same seven columns in
 * the same order, differing only in what a row *is* — a named account or a
 * monitored group — and therefore in the first column's width and the last
 * one's label.
 *
 * **Each sits below the card that summarises the same thing**: the card
 * answers "who leads"; the table is every one of them with their figures.
 *
 * **Client-side sort and page**, as in the prototype (the contract returns
 * every entry, ranked) — unless `paging` is given: then the view holds one
 * page the server already sorted, the block draws it as is, and a sort or page
 * click goes back to the owner. Only the columns the server can order by sort.
 *
 * Decisions the frame does not make:
 *
 *  1. **No `View all`.** The table already reaches every row. Dropped with the
 *     user 2026-09-24.
 *  2. **`Channel` and `Sentiment` are dominant values**, not properties — the
 *     one most of the row's conversation carries.
 *  3. **Columns sort**. Opens on `Conversations`, highest first.
 *  4. **`View ↗` is a no-op.** There are no profile or group URLs yet.
 */

/** Figma draws ten body rows, which is also what its pagination implies. */
export const RANKING_PAGE_SIZE = 10;
const PAGE_SIZE = RANKING_PAGE_SIZE;

/** One ranked voice or community, labelled and formatted. */
export type RankingEntryView = {
  value: string;
  label: string;
  channel: string;
  channelLabel: string;
  sentiment: string;
  sentimentLabel: string;
  conversations: number;
  conversationsLabel: string;
  engagement: number;
  engagementLabel: string;
  /**
   * Share of every conversation **in this cut**, so the column sums to 100.
   * `null` when the source cannot give the denominator — drawn as a dash.
   */
  share: number | null;
};

export type CommunityRankingView = {
  /** Most conversations first — or, server-paged, one page in the server's order. */
  entries: RankingEntryView[];
  /** The header badge's count, formatted. */
  countLabel: string;
  /** The count the badge reads, for its singular/plural. Defaults to `entries.length`. */
  count?: number;
};

export type RankingSortKey =
  'label' | 'channel' | 'sentiment' | 'conversations' | 'share' | 'engagement';
export type RankingSort = { key: RankingSortKey; direction: 'asc' | 'desc' };

export const DEFAULT_RANKING_SORT: RankingSort = { key: 'conversations', direction: 'desc' };

/** The three word columns sort A–Z; every figure opens highest first. */
const opensAscending = (key: RankingSortKey) =>
  key === 'label' || key === 'channel' || key === 'sentiment';

/** The sort a header click moves to — the table's rule, shared with server-paged owners. */
export function nextRankingSort(current: RankingSort, key: RankingSortKey): RankingSort {
  return current.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: opensAscending(key) ? 'asc' : 'desc' };
}

/** A server-paged table: the owner fetches; the block draws and reports clicks. */
export type RankingPaging = {
  /** 1-based, as served. */
  page: number;
  pageCount: number;
  sort: RankingSort;
  /** The columns the server can order by; the rest show no sort control. */
  sortable: readonly RankingSortKey[];
  onPageChange: (page: number) => void;
  onSortChange: (sort: RankingSort) => void;
};

function compare(a: RankingEntryView, b: RankingEntryView, key: RankingSortKey): number {
  switch (key) {
    case 'label':
      return a.label.localeCompare(b.label);
    case 'channel':
      return a.channelLabel.localeCompare(b.channelLabel) || a.label.localeCompare(b.label);
    case 'sentiment':
      return a.sentimentLabel.localeCompare(b.sentimentLabel) || a.label.localeCompare(b.label);
    case 'share':
      return (a.share ?? 0) - (b.share ?? 0);
    default:
      return a[key] - b[key];
  }
}

/** The table's order for a sort — exported for the parity test. */
export function sortRanking(
  entries: readonly RankingEntryView[],
  sort: RankingSort,
): RankingEntryView[] {
  const out = [...entries].sort((a, b) => compare(a, b, sort.key));
  return sort.direction === 'desc' ? out.reverse() : out;
}

export type CommunityRankingTableProps = WidgetBlockProps & {
  view?: CommunityRankingView;
  title: string;
  /** `voices` / `communities` — what the header badge counts. */
  noun: 'voices' | 'communities';
  /** Figma's last column: `Profile URL` or `Community URL`. */
  urlHeader: string;
  /** The frame's own widths, as shares of its 1192. */
  widths: readonly [string, string, string, string, string, string, string];
  /**
   * Clicking a row opens its conversations — `Top voices conversations`
   * (2235:119540) and `Top communities by mention conversations` (2235:120740).
   * The whole row activates and its leading cell shows the link on hover.
   * Omit and the rows stay inert.
   */
  onOpenEntry?: (entry: RankingEntryView) => void;
  /** Server-paged mode. Omit for the client-side sort and page. */
  paging?: RankingPaging;
};

export const CommunityRankingTable = memo(function CommunityRankingTable({
  status,
  view,
  title,
  noun,
  urlHeader,
  widths,
  span = 12,
  chrome,
  onOpenEntry,
  paging,
}: CommunityRankingTableProps) {
  const [localSort, setLocalSort] = useState<RankingSort>(DEFAULT_RANKING_SORT);
  const [localPage, setLocalPage] = useState(1);

  const entries = useMemo(() => (status === 'ready' && view ? view.entries : []), [status, view]);
  const sort = paging?.sort ?? localSort;
  const sorted = useMemo(
    () => (paging ? entries : sortRanking(entries, localSort)),
    [entries, localSort, paging],
  );

  const pageCount = paging
    ? Math.max(1, paging.pageCount)
    : Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  /* Clamped on read rather than reset in an effect — Influencers' reason. */
  const currentPage = Math.min(paging?.page ?? localPage, pageCount);
  const visible = paging
    ? sorted
    : sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const setPage = paging ? paging.onPageChange : setLocalPage;

  const sortFor = (key: RankingSortKey): TableHeaderSort =>
    sort.key === key ? sort.direction : 'selector';

  const onSort = (key: RankingSortKey) => () => {
    if (paging) {
      paging.onSortChange(nextRankingSort(paging.sort, key));
      return;
    }
    setLocalPage(1);
    setLocalSort((prev) => nextRankingSort(prev, key));
  };

  const sortable = (
    key: RankingSortKey,
    header: string,
    width: string,
    extra?: Partial<TableColumn>,
  ) =>
    paging && !paging.sortable.includes(key)
      ? { key, header, width, ...extra }
      : { key, header, width, sort: sortFor(key), onSort: onSort(key), ...extra };

  const columns: TableColumn[] = [
    sortable('label', 'Name', widths[0]),
    /* The marks carry no text, so the column head is the only thing naming them. */
    sortable('channel', 'Channel', widths[1], { cellType: 'actionIcons' }),
    sortable('sentiment', 'Sentiment', widths[2], { cellType: 'badge' }),
    sortable('conversations', 'Conversations', widths[3]),
    /*
     * **No `helpText` on a sortable column** (a button inside a button); the
     * explanation goes in the table header's own tooltip instead.
     */
    sortable('share', '% of overall conversations', widths[4]),
    sortable('engagement', 'Engagement', widths[5]),
    { key: 'url', header: urlHeader, cellType: 'actionButtons', width: widths[6] },
  ];

  const tableRows: TableRowData[] = visible.map((entry) => {
    /* A live channel's key is an id, so its mark is found by its name. */
    const platform = platformFor(entry.channel) ?? platformFor(entry.channelLabel);
    return {
      key: entry.value,
      onActivate: onOpenEntry ? () => onOpenEntry(entry) : undefined,
      activateLabel: onOpenEntry ? `Open conversations for ${entry.label}` : undefined,
      cells: {
        label: { text: entry.label },
        channel: {
          content: platform ? (
            <PlatformIcon name={platform.name} label={entry.channelLabel} />
          ) : (
            /* A channel with no drawn mark falls back to its name, as ChartLegend does. */
            entry.channelLabel
          ),
        },
        sentiment: {
          content: (
            <Badge type={SENTIMENT_BADGE[entry.sentiment] ?? 'gray'}>{entry.sentimentLabel}</Badge>
          ),
        },
        conversations: { text: entry.conversationsLabel },
        share: { text: entry.share === null ? '—' : `${entry.share.toFixed(1)}%` },
        engagement: { text: entry.engagementLabel },
        url: {
          content: (
            <Button
              hierarchy="ghostColor"
              iconTrailing={<Icon name="arrowUpRight" />}
              aria-label={`View ${entry.label}`}
            >
              View
            </Button>
          ),
        },
      },
    };
  });

  const count = view?.count ?? entries.length;

  return (
    <Table
      className={[spanClass(span), tableDraggingClass(chrome)].filter(Boolean).join(' ')}
      label={`${title}, ranked by conversations`}
      columns={columns}
      rows={tableRows}
      emptyLabel={tableEmptyLabel(status, `No ${noun} in this period`)}
      header={
        <TableHeader
          flash={chrome?.flash}
          title={title}
          badge={`${view && status === 'ready' ? view.countLabel : '0'} total ${count === 1 ? noun.replace(/e?s$/, '') : noun}`}
          helpText={
            noun === 'voices'
              ? 'The named accounts talking about us, ranked by how many conversations each one carried.'
              : 'The groups and forums our conversation happens in, ranked by how many conversations each one carried.'
          }
          helpSupportingText={`Channel and Sentiment are the most common value across that row's conversations — a plurality, not the whole picture. The share is of every conversation carrying a ${noun === 'voices' ? 'named account' : 'community'} in the period, not of every mention, so the column sums to 100 down the table.`}
          actions={<TableWidgetActions chrome={chrome} title={title} />}
        />
      }
      pagination={
        <Pagination
          page={currentPage}
          pageCount={pageCount}
          pages={pageWindow(currentPage, pageCount)}
          onPageChange={setPage}
          label={`${title} pages`}
        />
      }
    />
  );
});

type RankingPreset = Omit<CommunityRankingTableProps, 'title' | 'noun' | 'urlHeader' | 'widths'>;

/** Figma `2215:107393` — every named account, below the card that names the top one. */
export function TopVoicesTable(props: RankingPreset) {
  return (
    <CommunityRankingTable
      {...props}
      title="Top voices"
      noun="voices"
      urlHeader="Profile URL"
      /* The frame's own widths — 517 / 70 / 99 / 122 / 186 / 113 / 85 of 1192. */
      widths={['43.4%', '5.9%', '8.3%', '10.2%', '15.6%', '9.5%', '7.1%']}
    />
  );
}

/** Figma `2215:110242` — every monitored group, below the card that ranks them. */
export function TopCommunitiesTable(props: RankingPreset) {
  return (
    <CommunityRankingTable
      {...props}
      title="Top communities by mention"
      noun="communities"
      urlHeader="Community URL"
      /* The frame's own widths — 488 / 70 / 99 / 122 / 186 / 113 / 114 of 1192. */
      widths={['40.9%', '5.9%', '8.3%', '10.2%', '15.6%', '9.5%', '9.6%']}
    />
  );
}
